const EMAIL_FIELDS = [
    'Email Address',
    'Email Address (Office)',
    'Email Address (Personal)'
];

const PHONE_FIELDS = [
    'Phone Number',
    'Phone Number (Office)',
    'Phone Number (Mobile)',
    'Phone Number (Personal)',
    'Phone Number (Home)'
];

export const toNumber = (value) => {
    const parsed = Number(String(value ?? '').replace(/[$,]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

export const isTruthy = (value) => (
    value === true
    || value === 1
    || String(value || '').toLowerCase() === 'true'
);

export const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

export const daysSince = (value, now = Date.now()) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return (now - date.getTime()) / (1000 * 60 * 60 * 24);
};

export const getFieldValues = (row, editCategoryCommentsArray = []) => {
    const values = { ...(row?.children || {}) };
    (editCategoryCommentsArray || []).forEach(item => {
        if (item?.type) values[item.type] = item.value;
    });
    return values;
};

export const getRelationshipTotals = (opportunities = []) => {
    const totals = {
        total: 0,
        sales: 0,
        opportunities: 0,
        salesCount: 0,
        opportunityCount: 0,
        excludedCount: 0
    };

    (opportunities || []).forEach(opportunity => {
        const amount = toNumber(opportunity.OpportunityAmount);
        const isSale = String(opportunity.LinkType || '').toLowerCase() === 'sale'
            || isTruthy(opportunity.DidConvertToSale);
        const hasValueStatusTag = opportunity.ValueStatusTagID != null;
        const isValueStatusIncluded = hasValueStatusTag
            ? isTruthy(opportunity.ValueStatusFilterChecked)
            : !isTruthy(opportunity.StatusHide);

        if (!isValueStatusIncluded) {
            totals.excludedCount += 1;
            return;
        }

        totals.total += amount;
        if (isSale) {
            totals.sales += amount;
            totals.salesCount += 1;
        } else {
            totals.opportunities += amount;
            totals.opportunityCount += 1;
        }
    });

    return totals;
};

export const getEngagementInsights = ({
    notes = [],
    marketingData = {},
    companies = [],
    fieldValues = {},
    relationshipTotals = null
}) => {
    const totals = relationshipTotals || getRelationshipTotals(marketingData.opportunities || []);
    const activities = marketingData.activities || [];
    const workflows = marketingData.workflows || [];
    const reasons = [];
    let score = 0;

    const hasRecentNote = (notes || []).some(note => {
        const age = daysSince(note.date || note.timestamp);
        return age != null && age <= 90;
    });
    const hasRecentMarketingActivity = activities.some(activity => {
        const age = daysSince(activity.Timestamp || activity.SentTimestamp);
        return age != null && age <= 90;
    });
    const activeWorkflowCount = workflows.filter(workflow => workflow.Active === true || workflow.Active === 1).length;
    const hasOpenOpportunity = totals.opportunityCount > 0;
    const hasSale = totals.salesCount > 0;
    const hasRelationshipValue = totals.total > 0;
    const hasCampaignEngagement = activeWorkflowCount > 0 || activities.length > 0;
    const hasValidContactMethod = EMAIL_FIELDS.some(field => isValidEmail(fieldValues[field]))
        || PHONE_FIELDS.some(field => String(fieldValues[field] || '').trim() !== '');

    if (hasSale) {
        score += 25;
        reasons.push('Sales Relationship');
    }
    if (hasOpenOpportunity) {
        score += 20;
        reasons.push('Active Opportunity');
    }
    if (hasRelationshipValue) {
        score += 10;
        reasons.push('Relationship Value');
    }
    if (hasCampaignEngagement) {
        score += 15;
        reasons.push(activeWorkflowCount > 0 ? 'Campaign Workflow Engagement' : 'Marketing Activity Engagement');
    }
    if (hasRecentMarketingActivity || hasRecentNote) {
        score += 15;
        reasons.push(hasRecentMarketingActivity ? 'Recent Marketing Activity' : 'Recent Note');
    } else if ((notes || []).length > 0 || activities.length > 0) {
        score += 5;
        reasons.push('Historical Engagement');
    }
    if ((companies || []).length > 0) {
        score += 7;
        reasons.push('Linked Company');
    }
    if (hasValidContactMethod) {
        score += 8;
        reasons.push('Usable Contact Method');
    }
    if (reasons.length === 0) reasons.push('Needs More Data');

    return {
        score,
        reasons,
        percentage: Math.min(100, score)
    };
};

export const getRelationshipSignals = ({
    notes = [],
    marketingData = {}
}) => {
    const activities = marketingData.activities || [];
    const workflows = marketingData.workflows || [];
    const latestTouchDates = [
        ...(notes || []).map(note => note.date || note.timestamp),
        ...activities.map(activity => activity.Timestamp || activity.SentTimestamp)
    ].filter(Boolean)
        .map(value => new Date(value))
        .filter(date => !Number.isNaN(date.getTime()))
        .sort((a, b) => b.getTime() - a.getTime());
    const lastTouchDate = latestTouchDates.length > 0 ? latestTouchDates[0] : null;
    const touches90 = [
        ...(notes || []).filter(note => {
            const age = daysSince(note.date || note.timestamp);
            return age != null && age <= 90;
        }),
        ...activities.filter(activity => {
            const age = daysSince(activity.Timestamp || activity.SentTimestamp);
            return age != null && age <= 90;
        })
    ].length;
    const marketingCategories = [...new Set(activities.map(activity => String(activity.Category || '').trim()).filter(Boolean))];
    const signals = [];

    if (lastTouchDate) signals.push(`Last Touch ${lastTouchDate.toLocaleDateString('en-US', { timeZone: 'UTC' })}`);
    signals.push(`${touches90} Touch${touches90 === 1 ? '' : 'es'} In Last 90 Days`);
    signals.push(`${workflows.length} Workflow${workflows.length === 1 ? '' : 's'} Or Campaign${workflows.length === 1 ? '' : 's'}`);
    signals.push(`${marketingCategories.length} Marketing Categor${marketingCategories.length === 1 ? 'y' : 'ies'}`);

    return signals;
};
